/**
 * Bundle 128 - Dedicated /admin/incidents page.
 *
 * Fixes the Bundle 127 discoverability gap: the IncidentActionsPanel
 * was mounted at /admin/_debug/campaigns which had no sidebar link.
 * This page renders the same panel as its ONLY content, and the
 * link lives in the admin sidebar under "Incident actions".
 *
 * Also includes an audit log tail (Bundle 128 audit endpoint).
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { toast } from 'sonner';
import IncidentActionsPanel from './components/IncidentActionsPanel';
import IncidentDriftBanner from './components/IncidentDriftBanner';
import IncompleteSignupsTile from './components/IncompleteSignupsTile';
import BrokenNamesTile from './components/BrokenNamesTile';
import TestimonialsPendingReviewTile from './components/TestimonialsPendingReviewTile';
import PodStabilityTile from './components/PodStabilityTile';
import CrashesTile from './components/CrashesTile';
import AdvancedDiagnosticLookup from './components/AdvancedDiagnosticLookup';
import SendDigestNowButton from './components/SendDigestNowButton';

const AdminIncidentsPage = () => {
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAudit = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/incident/audit-log?limit=50');
      setAudit(data.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Could not load incident audit log.');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAudit(); }, []);

  return (
    <div className="p-8 max-w-7xl space-y-6" data-testid="admin-incidents-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary-900">
            Incident actions
          </h1>
          <p className="text-neutral-600 text-sm mt-1 max-w-3xl">
            Every recovery endpoint has a discoverable button here. Dry-run
            first, then Apply. Every apply is audit-logged with your identity
            and the reason (where applicable). Super admin only.
          </p>
        </div>
      </div>

      <IncidentDriftBanner />

      <div data-testid="ops-triage-tiles" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <IncompleteSignupsTile />
        <BrokenNamesTile />
        <TestimonialsPendingReviewTile />
        <PodStabilityTile />
        <CrashesTile />
      </div>

      <div className="flex items-center gap-2">
        <SendDigestNowButton />
      </div>

      <IncidentActionsPanel />

      <Card className="border shadow-soft">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-secondary-700">
                Audit log
              </div>
              <div className="text-sm text-primary-900 font-semibold">
                Last 50 incident actions
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={loadAudit}
                    data-testid="incident-audit-refresh">
              <RefreshCcw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="incident-audit-table">
              <thead className="bg-neutral-50 text-neutral-500 text-[10px] uppercase">
                <tr>
                  <th className="text-left p-2">When</th>
                  <th className="text-left p-2">Actor</th>
                  <th className="text-left p-2">Action</th>
                  <th className="text-left p-2">Target</th>
                  <th className="text-left p-2">Reason / result</th>
                </tr>
              </thead>
              <tbody>
                {audit.length === 0 ? (
                  <tr><td colSpan={5} className="p-4 text-center text-neutral-500">
                    {loading ? 'Loading.' : 'No incident actions recorded yet.'}
                  </td></tr>
                ) : audit.map((e) => (
                  <tr key={e.id} className="border-t border-neutral-100"
                        data-testid={`incident-audit-row-${e.id}`}>
                    <td className="p-2 text-neutral-500">
                      {e.created_at ? new Date(e.created_at).toLocaleString() : '-'}
                    </td>
                    <td className="p-2 text-primary-900">{e.actor_email || e.actor_user_id || '-'}</td>
                    <td className="p-2 font-mono text-[10px]">{e.action}</td>
                    <td className="p-2 font-mono text-[10px] text-neutral-500">
                      {(e.response_id || e.event_id || '-').slice(0, 20)}
                    </td>
                    <td className="p-2 text-neutral-700 truncate max-w-md">
                      {e.reason
                        || (e.result && JSON.stringify(e.result).slice(0, 100))
                        || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <AdvancedDiagnosticLookup />
    </div>
  );
};

export default AdminIncidentsPage;
